import { useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { PLAYER } from "../../../../../common/constants.ts";
import type { GameAttributesLeague, Phase, View } from "../../../../../common/types.ts";
import { groupByUnique } from "../../../../../common/utils.ts";
import { Mood } from "../../components/Mood.tsx";
import { MobileStatusMetric } from "../../components/MobileStatusMetric.tsx";
import { PlayerCardList } from "../../components/PlayerCardList.tsx";
import { PlayerFaceThumb } from "../../components/PlayerFaceThumb.tsx";
import {
  MobileContentHero,
  MobileEntityRow,
  MobileSection,
} from "../../mobile/mobilePrimitives.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import { isMobileViewport } from "../../hooks/useIsMobileViewport.ts";
import { confirmDangerousAction } from "../../util/dangerConfirm.ts";
import { decisionConfirmVariants } from "../../util/decisionConfirmVariants.ts";
import { formatPlayerName } from "../../util/playerNames.ts";
import { helpers } from "../../util/helpers.ts";
import { formatSeasonRecord } from "../../util/seasonRecord.ts";
import i18n from "../../util/i18n.ts";
import { useLocalPartial } from "../../util/local.ts";
import { logEvent } from "../../util/logEvent.ts";
import { createMobileCrossTabHref } from "../../util/mobileCrossTabLink.ts";
import { createMobileReturnUrl } from "../../util/mobilePageContext.ts";
import { PositionText } from "../../util/positionNames.tsx";
import { pronounLocalized } from "../../util/pronounLocalized.ts";
import { translateTeamName, translateTeamRegion } from "../../util/teamNames.ts";
import { toWorker } from "../../util/toWorker.ts";
import { InjuriesSheet } from "./InjuriesSheet.tsx";
import { TeamHistorySheet } from "./TeamHistorySheet.tsx";
import {
  TeamFinanceSheets,
  type TeamFinanceSheet,
} from "./TeamFinanceSheets.tsx";
import {
  canUseRosterManagementMode,
  getRosterActionVisibility,
  hasRosterCardActions,
} from "./actionVisibility.ts";
import PlayingTime from "./PlayingTime.tsx";
import { PlayerQuickSheet } from "./PlayerQuickSheet.tsx";
import {
  buildRosterReadinessModel,
  getRosterPrimaryActionPlan,
  type RosterPrimaryActionSurface,
  type RosterReadinessModel,
} from "./readiness.ts";
import {
  createOpponentFinanceMobileHref,
  resolveOpponentFinanceScope,
} from "../teamManagementDepth.ts";
import {
  getCapSpaceStatus,
  getHealthScore,
  getRosterStatus,
} from "../LeagueDashboard/statusMetrics.ts";

type RosterPlayer = View<"roster">["players"][number];

const isPlayerInjured = (p: RosterPlayer) =>
  p.injury.gamesRemaining > 0 || p.injury.type !== "Healthy";

const getMobileScrollY = () => (typeof window !== "undefined" ? window.scrollY : undefined);

const defaultRosterReturnLabel = () =>
  i18n.t("roster.returnToMyRoster", {
    ns: "ui",
    defaultValue: "Return to my roster",
  });

const createRosterPlayerHref = (
  pid: number,
  group: string,
  returnLabel = defaultRosterReturnLabel(),
) =>
  createMobileReturnUrl(helpers.leagueUrl(["player", pid]), {
    activeTab: group,
    filterKey: "rosterGroup",
    filterValue: group,
    pageID: "roster",
    returnLabel,
    scrollY: getMobileScrollY(),
  });

const handleRelease = async (
  p: RosterPlayer,
  phase: Phase,
  season: number,
  gender: GameAttributesLeague["gender"],
) => {
  const wasPlayerJustDrafted = helpers.justDrafted(p, phase, season);

  let releaseMessage;
  const playerName = formatPlayerName(p.firstName, p.lastName);
  if (wasPlayerJustDrafted) {
    releaseMessage = i18n.t("roster.releaseConfirmDrafted", {
      ns: "ui",
      firstName: p.firstName,
      lastName: p.lastName,
      defaultValue: `Are you sure you want to release ${playerName}? ${pronounLocalized(gender, "He")} will become a free agent and no longer take up a roster spot on your team. Because you just drafted ${pronounLocalized(gender, "him")} and the regular season has not started yet, you will not have to pay ${pronounLocalized(gender, "his")} contract.`,
    });
  } else {
    releaseMessage = i18n.t("roster.releaseConfirm", {
      ns: "ui",
      firstName: p.firstName,
      lastName: p.lastName,
      exp: p.contract.exp,
      defaultValue: `Are you sure you want to release ${playerName}? ${pronounLocalized(gender, "He")} will become a free agent and no longer take up a roster spot on your team, but you will still have to pay ${pronounLocalized(gender, "his")} salary (and have it count against the salary cap) until ${pronounLocalized(gender, "his")} contract expires in ${p.contract.exp}.`,
    });
  }

  const proceed = await confirmDangerousAction(releaseMessage, {
    kind: "destructive",
    okText: i18n.t("roster.releasePlayer", {
      ns: "ui",
      defaultValue: "Release player",
    }),
    variant: decisionConfirmVariants.rosterRelease,
  });
  if (proceed) {
    const errorMsg = await toWorker("main", "releasePlayer", {
      pids: [p.pid],
    });
    if (errorMsg) {
      logEvent({
        type: "error",
        text: errorMsg,
        saveToDb: false,
      });
    }
  }
};

const readinessDescription = (model: RosterReadinessModel) => {
  switch (model.kind) {
    case "needsPlayers":
      return i18n.t("roster.readinessNeedsPlayersDescription", {
        ns: "ui",
        count: model.minimumGap,
        defaultValue: "{{count}} more player(s) are needed to reach the roster minimum.",
      });
    case "tooManyPlayers":
      return i18n.t("roster.readinessTooManyPlayersDescription", {
        ns: "ui",
        count: Math.abs(model.openRosterSpots),
        defaultValue: "Release or trade {{count}} player(s) to get under the roster limit.",
      });
    case "needsHealthyPlayers":
      return i18n.t("roster.readinessNeedsHealthyPlayersDescription", {
        ns: "ui",
        count: model.starterGap,
        defaultValue: "{{count}} more healthy player(s) are needed for the active lineup.",
      });
    case "payrollRisk":
      return i18n.t("roster.readinessPayrollRiskDescription", {
        ns: "ui",
        defaultValue: "The team can play, but payroll rules need attention soon.",
      });
    default:
      return undefined;
  }
};

// B2c (v5Content): the first screen IS the roster content — health/rotation
// status as stat rows plus the starting five entity rows (children). The old
// DecisionHero narration (eyebrow, status title, "next issue" cell) and the
// duplicate starters/rotation preview section are gone (法则一/法则二); the
// only guidance is the one explainer line, and it only renders when the
// readiness model actually needs attention.
const RosterReadinessHero = ({
  children,
  maxRosterSize,
  minRosterSize,
  payroll,
  playerCount,
  primaryActionSurface,
  readiness,
  salaryCap,
  salaryCapType,
  showRosterManagementToggle,
  teamColors,
}: Pick<
  View<"roster">,
  | "maxRosterSize"
  | "minRosterSize"
  | "numPlayersOnCourt"
  | "payroll"
  | "salaryCap"
  | "salaryCapType"
> & {
  children?: ReactNode;
  playerCount: number;
  primaryActionSurface: RosterPrimaryActionSurface;
  readiness: RosterReadinessModel;
  showRosterManagementToggle: boolean;
  teamColors?: [string, string, string];
}) => {
  const salarySpace =
    salaryCapType === "none" || payroll === undefined ? undefined : salaryCap - payroll;
  const injuredCount = Math.max(0, playerCount - readiness.healthyCount);
  const rosterStatus = getRosterStatus({
    count: playerCount,
    max: maxRosterSize,
    min: minRosterSize,
  });
  const capSpaceStatus = getCapSpaceStatus({
    capSpace: salarySpace,
    payrollLimit: salaryCap,
  });
  const rosterStatusText =
    rosterStatus.state === "short"
      ? i18n.t("leagueDashboard.rosterNeedsPlayers", {
          ns: "ui",
          count: rosterStatus.missing,
          defaultValue: "Need {{count}} more player(s)",
        })
      : rosterStatus.state === "full"
        ? i18n.t("leagueDashboard.rosterFullStatus", {
            ns: "ui",
            defaultValue: "Roster is full",
          })
        : i18n.t("leagueDashboard.rosterCompleteStatus", {
            ns: "ui",
            defaultValue: "Roster complete",
          });
  const salaryStatusText =
    capSpaceStatus.state === "over"
      ? i18n.t("leagueDashboard.capOverStatus", {
          ns: "ui",
          amount: helpers.formatCurrency(Math.abs(salarySpace!), "M"),
          defaultValue: "{{amount}} over the limit",
        })
      : capSpaceStatus.state === "near"
        ? i18n.t("leagueDashboard.capNearStatus", {
            ns: "ui",
            defaultValue: "Near the limit",
          })
        : capSpaceStatus.state === "available"
          ? i18n.t("leagueDashboard.capAvailableStatus", {
              ns: "ui",
              amount: helpers.formatCurrency(salarySpace!, "M"),
              defaultValue: "{{amount}} available",
            })
          : i18n.t("common.noData", { ns: "ui", defaultValue: "No data yet." });

  // A4: the outer roster owns the action plan. Urgent market/payroll work stays
  // in the hero even when roster management is technically available, while a
  // manage-roster plan moves to the fixed mobile action without duplication.
  const action =
    primaryActionSurface === "hero" ? (
      <a
        className="btn btn-primary"
        data-roster-primary-action=""
        href={createMobileCrossTabHref(helpers.leagueUrl(["market"]))}
      >
        {readiness.action === "findPlayers"
          ? i18n.t("roster.findPlayers", {
              ns: "ui",
              defaultValue: "Find players",
            })
          : i18n.t("roster.manageRoster", {
                ns: "ui",
                defaultValue: "Manage roster",
              })}
      </a>
    ) : undefined;

  const explainer = readinessDescription(readiness);

  return (
    <MobileContentHero
      action={action}
      // C2: team-primary-color tint (low-opacity gradient over the theme
      // background — text contrast holds in light and dark themes).
      className={`mb-3 roster-readiness-${readiness.severity}${
        teamColors ? " mobile-team-hero-tint" : ""
      }`}
      data-roster-readiness-model=""
      data-roster-readiness-state={readiness.kind}
      data-roster-management-available={showRosterManagementToggle ? "true" : "false"}
      data-team-color-hero={teamColors ? "true" : undefined}
      data-v5-roster-status-first-screen=""
      explainer={explainer}
      style={
        teamColors
          ? ({
              "--mobile-team-color-primary": teamColors[0],
              "--mobile-team-color-secondary": teamColors[1],
            } as CSSProperties)
          : undefined
      }
    >
      <div
        className="roster-status-metric-grid"
        data-roster-readiness-metrics=""
        data-v5-roster-status-rows=""
      >
        <MobileStatusMetric
          label={i18n.t("roster.playersSigned", {
            ns: "ui",
            defaultValue: "Players",
          })}
          score={rosterStatus.score}
          value={rosterStatusText}
        />
        <MobileStatusMetric
          data-roster-health-metric=""
          label={i18n.t("roster.healthyPlayers", {
            ns: "ui",
            defaultValue: "Healthy",
          })}
          score={getHealthScore(injuredCount)}
          value={
            injuredCount === 0
              ? i18n.t("leagueDashboard.allHealthyStatus", {
                  ns: "ui",
                  defaultValue: "All healthy",
                })
              : i18n.t("leagueDashboard.injuredCountStatus", {
                  ns: "ui",
                  count: injuredCount,
                  defaultValue: "{{count}} injured",
                })
          }
        />
        {salarySpace !== undefined ? (
          <MobileStatusMetric
            label={i18n.t("roster.salarySpace", {
              ns: "ui",
              defaultValue: "Salary space",
            })}
            score={capSpaceStatus.score}
            value={salaryStatusText}
          />
        ) : null}
      </div>
      {children}
    </MobileContentHero>
  );
};

const Roster = ({
  abbrev,
  challengeNoRatings,
  currentSeason,
  editable,
  luxuryTaxAmount,
  maxRosterSize,
  minRosterSize,
  minPayrollAmount,
  numPlayersOnCourt,
  payroll,
  phase,
  players,
  playoffs,
  salaryCap,
  salaryCapType,
  season,
  showSpectatorWarning,
  showRelease,
  showTradeFor,
  t,
  teamHistorySummary,
  teamFinances,
  tid,
  userTid,
}: View<"roster">) => {
  const [editingRoster, setEditingRoster] = useState(false);
  const [rosterDepthOpen, setRosterDepthOpen] = useState(false);
  const [injuriesSheetOpen, setInjuriesSheetOpen] = useState(false);
  const [teamHistorySheetOpen, setTeamHistorySheetOpen] = useState(false);
  const [teamFinanceSheet, setTeamFinanceSheet] = useState<TeamFinanceSheet | undefined>();
  const [playerQuickSheet, setPlayerQuickSheet] = useState<
    | {
        group: string;
        player: RosterPlayer;
      }
    | undefined
  >(undefined);
  const [sortedPids, setSortedPids] = useState<number[] | undefined>(undefined);
  const [prevPlayers, setPrevPlayers] = useState(players);
  const { gender } = useLocalPartial(["gender"]);
  const teamRouteParam = `${abbrev}_${tid}`;
  const teamDisplayName = `${translateTeamRegion(t.region)} ${translateTeamName(t.name)}`;
  const isHistoricalSeason = season !== currentSeason;
  const isPlayoffRoster = playoffs === "playoffs";
  const isCurrentRegularSeason = !isHistoricalSeason && !isPlayoffRoster;
  const isOtherTeam = tid !== userTid;
  const canEditRoster = editable && isCurrentRegularSeason;
  const canReleaseRoster = showRelease && isCurrentRegularSeason;
  const canTradeForRoster = showTradeFor && isCurrentRegularSeason;
  const isOtherTeamCurrentRoster = isOtherTeam && isCurrentRegularSeason;
  const spectatorOpponentRoster = isOtherTeamCurrentRoster && !showTradeFor;
  const isUserCurrentRoster = tid === userTid && isCurrentRegularSeason;
  const shouldFoldRosterDepth = isOtherTeamCurrentRoster || !isCurrentRegularSeason;
  const opponentFinanceScope = resolveOpponentFinanceScope({
    currentSeason,
    playoffs,
    season,
  });
  const opponentFinanceHref = isOtherTeam
    ? helpers.leagueUrl([
        "team_finances",
        teamRouteParam,
        ...(opponentFinanceScope === "all" ? ["all"] : []),
      ])
    : undefined;
  const rosterTitle = isPlayoffRoster
    ? i18n.t("roster.playoffRosterTitle", {
        ns: "ui",
        season,
        defaultValue: "{{season}} playoff roster",
      })
    : i18n.t("roster.title", { ns: "ui", defaultValue: "Roster" });

  useTitleBar({
    title: rosterTitle,
    // The Team tab is a fixed management workspace for the user's current
    // team. Team/season/split selectors only belong to roster pages reached as
    // scouting or archive destinations, not to the Team tab itself.
    dropdownView: isUserCurrentRoster ? undefined : "roster",
    dropdownFields: isUserCurrentRoster
      ? undefined
      : {
          teams: abbrev,
          seasons: season,
          playoffsCombined: playoffs,
        },
    moreInfoAbbrev: abbrev,
    moreInfoSeason: season,
    moreInfoTid: tid,
  });

  if (players !== prevPlayers) {
    setSortedPids(undefined);
    setPrevPlayers(players);
  }

  let playersSorted: typeof players;
  if (sortedPids !== undefined) {
    const playersByPid = groupByUnique(players, "pid");
    playersSorted = sortedPids.map((pid) => playersByPid[pid]).filter((p) => p !== undefined);
  } else {
    playersSorted = players;
  }

  const rosterIndexByPid = new Map(playersSorted.map((p, index) => [p.pid, index]));
  const showMood = season === currentSeason;
  const showRosterManagementToggle = canUseRosterManagementMode({
    editable: canEditRoster,
    playerCount: playersSorted.length,
    showRelease: canReleaseRoster,
  });
  const rosterReadiness = buildRosterReadinessModel({
    editable: canEditRoster,
    injuredCount: playersSorted.filter(isPlayerInjured).length,
    luxuryTaxAmount: luxuryTaxAmount ?? 0,
    maxRosterSize,
    minPayrollAmount: minPayrollAmount ?? 0,
    minRosterSize,
    numPlayersOnCourt,
    playerCount: playersSorted.length,
  });
  const rosterPrimaryActionSurface = getRosterPrimaryActionPlan({
    action: rosterReadiness.action,
    managementAvailable: showRosterManagementToggle,
  });
  const fixedManagementAvailable = rosterPrimaryActionSurface === "fixedManagement";
  const rosterManagementActive = fixedManagementAvailable && editingRoster;
  // 法则二: the starting five already render once in the hero, so the folded
  // complete roster only mounts its player cards when actually opened. Hoisted
  // so both the unified team-action row (toggle) and the section below read it.
  const fullRosterOpen = rosterDepthOpen || (rosterManagementActive && !shouldFoldRosterDepth);

  useEffect(() => {
    if (!fixedManagementAvailable && editingRoster) {
      setEditingRoster(false);
    }
  }, [editingRoster, fixedManagementAvailable]);

  // R1-1: intent parameter channel. Cross-tab CTAs deep-link to a roster-hosted
  // sheet with the generic `?m_open=<sheetKey>` convention (promoted from the
  // home InboxSheet `?m_open_mid` landing). The host reads it once on mount,
  // opens the target sheet, and strips the param via history.replaceState so a
  // refresh/back does not reopen it — one-shot, never entering the back stack.
  // The injuries report sheet is the first adopter.
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const open = params.get("m_open");
    if (open === null) {
      return;
    }

    if (open === "injuries" && isUserCurrentRoster) {
      setInjuriesSheetOpen(true);
    }

    params.delete("m_open");
    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", nextUrl);
  }, []);

  const reorderRoster = async (newSortedPids: number[]) => {
    setSortedPids(newSortedPids);
    await toWorker("main", "reorderRoster", newSortedPids);
  };

  const movePlayer = async (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= playersSorted.length) {
      return;
    }

    const newSortedPids = playersSorted.map((p) => p.pid);
    const [pid] = newSortedPids.splice(index, 1);
    if (pid === undefined) {
      return;
    }
    newSortedPids.splice(newIndex, 0, pid);
    await reorderRoster(newSortedPids);
  };

  const renderMoveButtons = (index: number) => {
    const moveUpLabel = i18n.t("roster.moveUp", {
      ns: "ui",
      defaultValue: "Move up",
    });
    const moveDownLabel = i18n.t("roster.moveDown", {
      ns: "ui",
      defaultValue: "Move down",
    });

    return (
      <div className="btn-group w-100">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={index === 0}
          aria-label={moveUpLabel}
          onClick={(event) => {
            event.stopPropagation();
            void movePlayer(index, -1);
          }}
        >
          {moveUpLabel}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={index === playersSorted.length - 1}
          aria-label={moveDownLabel}
          onClick={(event) => {
            event.stopPropagation();
            void movePlayer(index, 1);
          }}
        >
          {moveDownLabel}
        </button>
      </div>
    );
  };

  const getRole = (p: RosterPlayer) => {
    const index = rosterIndexByPid.get(p.pid) ?? 0;
    if (index < numPlayersOnCourt) {
      return i18n.t("playerCard.roleStarter", {
        ns: "ui",
        defaultValue: "Starter",
      });
    }
    if (p.ptModifier >= 1.25) {
      return i18n.t("playerCard.rolePriority", {
        ns: "ui",
        defaultValue: "Priority",
      });
    }
    if (p.ptModifier <= 0.75) {
      return i18n.t("playerCard.roleLimited", {
        ns: "ui",
        defaultValue: "Limited",
      });
    }
    return i18n.t("playerCard.roleRotation", {
      ns: "ui",
      defaultValue: "Rotation",
    });
  };

  const getReasonTags = (p: RosterPlayer) => {
    const index = rosterIndexByPid.get(p.pid) ?? 0;
    const tags = [];

    if (isPlayerInjured(p)) {
      tags.push(
        index < numPlayersOnCourt
          ? i18n.t("playerCard.tagStarterRisk", {
              ns: "ui",
              defaultValue: "Starter risk",
            })
          : i18n.t("playerCard.tagInjured", {
              ns: "ui",
              defaultValue: "Injured",
            }),
      );
    }
    if (!challengeNoRatings && p.ratings.pot - p.ratings.ovr >= 12) {
      tags.push(
        i18n.t("playerCard.tagHighUpside", {
          ns: "ui",
          defaultValue: "High upside",
        }),
      );
    }
    if (season === currentSeason && p.contract.amount >= 15000) {
      tags.push(
        i18n.t("playerCard.tagExpensive", {
          ns: "ui",
          defaultValue: "Expensive",
        }),
      );
    }
    if (season === currentSeason && p.contract.exp <= season) {
      tags.push(
        i18n.t("playerCard.tagExpiring", {
          ns: "ui",
          defaultValue: "Expiring",
        }),
      );
    }

    return tags.slice(0, 3);
  };

  const starterPlayers = playersSorted.slice(0, numPlayersOnCourt);
  const benchPlayers = playersSorted.slice(numPlayersOnCourt);
  const rotationPlayers = benchPlayers.filter((p) => p.ptModifier > 0);
  const reservePlayers = benchPlayers.filter((p) => p.ptModifier <= 0);
  const scoutedRosterReturnLabel = i18n.t("roster.returnToScoutedRoster", {
    ns: "ui",
    defaultValue: "Return to scouted roster",
  });
  const seedOpponentFinanceReturn = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      opponentFinanceHref === undefined ||
      typeof window === "undefined" ||
      !isMobileViewport() ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.currentTarget.href = createOpponentFinanceMobileHref({
      financeHref: opponentFinanceHref,
      returnHref: `${window.location.pathname}${window.location.search}`,
      returnLabel: scoutedRosterReturnLabel,
      scrollY: window.scrollY,
    });
  };
  const getRosterPlayerHref = (pid: number, group: string) =>
    createRosterPlayerHref(
      pid,
      group,
      isOtherTeam ? scoutedRosterReturnLabel : defaultRosterReturnLabel(),
    );
  const openPlayerQuickSheet = (
    event: MouseEvent<HTMLAnchorElement>,
    player: RosterPlayer,
    group: string,
  ) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    setPlayerQuickSheet({ group, player });
  };
  const corePlayers = starterPlayers.slice(0, 3);
  const corePids = new Set(corePlayers.map((p) => p.pid));
  // 法则二: trade targets never re-list the core three shown right beside them.
  const tradeTargetPlayers = playersSorted
    .filter((p) => !p.untradable && !corePids.has(p.pid))
    .slice(0, 3);

  const renderScoutingList = ({
    emptyText,
    players: groupPlayers,
    testID,
  }: {
    emptyText: string;
    players: typeof playersSorted;
    testID: string;
  }) => (
    <div className="list-group list-group-flush">
      {groupPlayers.length > 0 ? (
        groupPlayers.map((p) => (
          <a
            className="list-group-item list-group-item-action px-0 d-flex justify-content-between gap-3"
            data-roster-player-quick-pid={p.pid}
            href={getRosterPlayerHref(p.pid, testID)}
            key={p.pid}
            onClick={(event) => openPlayerQuickSheet(event, p, testID)}
          >
            <span>
              <span className="fw-semibold">{formatPlayerName(p.firstName, p.lastName)}</span>
              <span className="d-block small text-body-secondary">{getRole(p)}</span>
            </span>
            <span className="small text-end text-nowrap">
              {!challengeNoRatings || p.tid === PLAYER.RETIRED
                ? `${p.ratings.ovr}/${p.ratings.pot}`
                : i18n.t("roster.scoutingRatingsHidden", {
                    ns: "ui",
                    defaultValue: "Ratings hidden",
                  })}
            </span>
          </a>
        ))
      ) : (
        <p className="text-body-secondary small mb-0">{emptyText}</p>
      )}
    </div>
  );

  const renderRosterGroup = ({
    emptyText,
    players: groupPlayers,
    testID,
    title,
  }: {
    emptyText: string;
    players: typeof playersSorted;
    testID: string;
    title: string;
  }) => (
    <MobileSection className="mb-3" data-roster-group={testID} title={title}>
      {groupPlayers.length > 0 ? (
        <PlayerCardList
          swipeRevealActions
          players={groupPlayers}
          season={season}
          shouldShowRatings={(p) => !challengeNoRatings || p.tid === PLAYER.RETIRED}
          getJerseyNumber={(p) => p.stats.jerseyNumber}
          getOvrChange={(p) => p.ratings.dovr}
          getPotChange={(p) => p.ratings.dpot}
          getAwards={(p) => p.awards}
          getContract={(p) => (season === currentSeason ? p.contract : undefined)}
          getPlayerHref={(p) => getRosterPlayerHref(p.pid, testID)}
          onPlayerNameClick={(event, p) => openPlayerQuickSheet(event, p, testID)}
          getReasonTags={getReasonTags}
          getRole={getRole}
          renderMood={(p) =>
            showMood && p.tid === tid ? (
              <Mood
                defaultType="current"
                host="roster"
                maxWidth
                p={p}
                playerHref={getRosterPlayerHref(p.pid, testID)}
              />
            ) : null
          }
          renderActions={(p) => {
            const index = rosterIndexByPid.get(p.pid) ?? -1;
            const visibility = getRosterActionVisibility({
              editable: canEditRoster,
              managementActive: rosterManagementActive,
              playerIndex: index,
              showRelease: canReleaseRoster,
              showTradeFor: canTradeForRoster,
            });
            if (!hasRosterCardActions(visibility)) {
              return null;
            }

            return (
              <div className="d-flex flex-column gap-1">
                {visibility.details ? (
                  <a
                    className="btn btn-outline-secondary btn-sm w-100"
                    href={getRosterPlayerHref(p.pid, testID)}
                  >
                    {i18n.t("common.details", {
                      ns: "ui",
                      defaultValue: "Details",
                    })}
                  </a>
                ) : null}
                {visibility.playingTime || visibility.reorder ? (
                  <>
                    {visibility.playingTime ? <PlayingTime p={p} userTid={userTid} /> : null}
                    {visibility.reorder ? renderMoveButtons(index) : null}
                  </>
                ) : null}
                {visibility.release ? (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm w-100"
                    data-roster-release-action-pid={p.pid}
                    disabled={!p.canRelease}
                    onClick={() => handleRelease(p, phase, currentSeason, gender)}
                  >
                    {i18n.t("roster.release", {
                      ns: "ui",
                      defaultValue: "Release",
                    })}
                  </button>
                ) : null}
                {visibility.tradeFor ? (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm w-100"
                    disabled={p.untradable}
                    onClick={() => {
                      toWorker("actions", "tradeFor", { pid: p.pid });
                    }}
                  >
                    {i18n.t("roster.tradeFor", {
                      ns: "ui",
                      defaultValue: "Trade for",
                    })}
                  </button>
                ) : null}
              </div>
            );
          }}
        />
      ) : (
        <p className="text-body-secondary small mb-0">{emptyText}</p>
      )}
    </MobileSection>
  );

  const rosterGroups = (
    <>
      {renderRosterGroup({
        emptyText: i18n.t("roster.noStarters", {
          ns: "ui",
          defaultValue: "No starters are available.",
        }),
        players: starterPlayers,
        testID: "starter-unit",
        title: i18n.t("roster.starterUnit", {
          ns: "ui",
          defaultValue: "Starter unit",
        }),
      })}
      {renderRosterGroup({
        emptyText: i18n.t("roster.noRotationPlayers", {
          ns: "ui",
          defaultValue: "No bench players are currently in the rotation.",
        }),
        players: rotationPlayers,
        testID: "rotation-list",
        title: i18n.t("roster.rotationList", {
          ns: "ui",
          defaultValue: "Rotation",
        }),
      })}
      {renderRosterGroup({
        emptyText: i18n.t("roster.noReservePlayers", {
          ns: "ui",
          defaultValue: "No reserves are pinned to the bench.",
        }),
        players: reservePlayers,
        testID: "reserve-list",
        title: i18n.t("roster.reserveList", {
          ns: "ui",
          defaultValue: "Reserves",
        }),
      })}
    </>
  );

  const fixedManagementActionLabel = rosterManagementActive
    ? i18n.t("roster.finishManagingRoster", {
        ns: "ui",
        defaultValue: "Done",
      })
    : i18n.t("roster.p0s08FixedManageRoster", {
        ns: "ui",
        defaultValue: "Manage game rotation",
      });

  // The roster preview and its expanded state are one list. Expanding appends
  // the remaining players to this same container, so the starting unit is not
  // mounted a second time in a separate "full roster" list.
  const overviewPlayers = fullRosterOpen ? playersSorted : starterPlayers;
  const starterRows =
    !isOtherTeamCurrentRoster && !rosterManagementActive ? (
      <div className="mt-3" data-v5-roster-starting-five="">
        <div
          data-roster-unified-player-list=""
          data-roster-unified-player-list-expanded={fullRosterOpen ? "true" : "false"}
          data-roster-unified-player-list-total={playersSorted.length}
          data-roster-unified-player-list-visible={overviewPlayers.length}
          id="roster-unified-player-list"
        >
          {overviewPlayers.length > 0 ? (
            overviewPlayers.map((p) => {
              const primaryTag = getReasonTags(p)[0];

              return (
                <MobileEntityRow
                  data-roster-unified-player-pid={p.pid}
                  key={p.pid}
                  media={
                    // C2: face thumbnail in the B1-reserved media slot (lazy +
                    // cached inside PlayerFaceThumb).
                    <PlayerFaceThumb
                      colors={t.colors}
                      face={p.face}
                      imgURL={p.imgURL}
                      jersey={t.jersey}
                      pid={p.pid}
                    />
                  }
                  meta={
                    <>
                      <PositionText pos={p.ratings.pos} />
                      {primaryTag ? (
                        <span className="badge text-bg-light border ms-2">{primaryTag}</span>
                      ) : null}
                    </>
                  }
                  title={
                    <a
                      data-player-card-link-pid={p.pid}
                      href={getRosterPlayerHref(p.pid, "starter-focus")}
                      onClick={(event) => openPlayerQuickSheet(event, p, "starter-focus")}
                    >
                      {formatPlayerName(p.firstName, p.lastName)}
                    </a>
                  }
                  trailing={
                    <span className="small text-nowrap">
                      {!challengeNoRatings || p.tid === PLAYER.RETIRED
                        ? `${p.ratings.ovr}/${p.ratings.pot}`
                        : i18n.t("roster.scoutingRatingsHidden", {
                            ns: "ui",
                            defaultValue: "Ratings hidden",
                          })}
                    </span>
                  }
                />
              );
            })
          ) : (
            <p className="text-body-secondary small mb-0">
              {i18n.t("roster.noStarters", {
                ns: "ui",
                defaultValue: "No starters are available.",
              })}
            </p>
          )}
        </div>
        {playersSorted.length > starterPlayers.length ? (
          <button
            aria-controls="roster-unified-player-list"
            aria-expanded={fullRosterOpen}
            className="btn btn-link w-100 mt-2"
            data-roster-full-roster-toggle=""
            data-roster-team-action="full_roster"
            onClick={() => setRosterDepthOpen((value) => !value)}
            type="button"
          >
            {i18n.t(
              fullRosterOpen
                ? "roster.p0s08CollapseRosterSummary"
                : "roster.p0s08FullRosterSummary",
              {
                ns: "ui",
                defaultValue: fullRosterOpen ? "Collapse roster" : "Open full roster",
              },
            )}
            <span aria-hidden="true" className="ms-1">
              {fullRosterOpen ? "▴" : "▾"}
            </span>
          </button>
        ) : null}
      </div>
    ) : null;

  return (
    <div
      data-host-state={canEditRoster ? "ready" : "readonly"}
      data-host-state-host="roster"
      data-host-state-row-count={playersSorted.length}
      data-v5-roster-content-first-screen=""
    >
      {/* N3-C: scouting another team no longer routes to a standalone (low-value)
          team-history page. Its all-time record / title count / best season is
          folded into this one-line franchise summary at the top of the opponent
          roster; the team_history route stays live for deep links. */}
      {isOtherTeamCurrentRoster && teamHistorySummary ? (
        <div className="alert alert-secondary py-2 mb-3 small" data-roster-team-history-summary="">
          <span className="fw-semibold">
            {i18n.t("roster.franchiseHistoryLabel", {
              ns: "ui",
              defaultValue: "Franchise history",
            })}
          </span>{" "}
          {i18n.t("roster.franchiseAllTimeRecord", {
            ns: "ui",
            defaultValue: "{{record}} all-time",
            record: formatSeasonRecord({
              won: teamHistorySummary.totalWon,
              lost: teamHistorySummary.totalLost,
              otl: teamHistorySummary.totalOtl,
              tied: teamHistorySummary.totalTied,
            }),
          })}
          {teamHistorySummary.championships > 0
            ? ` · ${i18n.t("roster.franchiseTitles", {
                ns: "ui",
                count: teamHistorySummary.championships,
                defaultValue: "{{count}} title(s)",
              })}`
            : null}
          {teamHistorySummary.bestRecord
            ? ` · ${i18n.t("roster.franchiseBestSeason", {
                ns: "ui",
                season: teamHistorySummary.bestRecord.season,
                record: formatSeasonRecord({
                  won: teamHistorySummary.bestRecord.won,
                  lost: teamHistorySummary.bestRecord.lost,
                  otl: teamHistorySummary.bestRecord.otl,
                  tied: teamHistorySummary.bestRecord.tied,
                }),
                defaultValue: "Best {{season}} ({{record}})",
              })}`
            : null}
          {/* M3-1: the deep team-history surface is now a sheet, reachable for
              the scouted opponent too (the sheet fetches that team's tid). */}
          <div className="mt-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              data-roster-team-action="team_history"
              onClick={() => setTeamHistorySheetOpen(true)}
              type="button"
            >
              {i18n.t("roster.teamHistoryLink", {
                ns: "ui",
                defaultValue: "Team history",
              })}
            </button>
          </div>
        </div>
      ) : null}
      {/* N1: the historical / other-team "return to current roster" link is
          replaced by the global TitleBar back control (mobileBackStack). */}
      {isHistoricalSeason ? (
        <div className="alert alert-secondary">
          <div className="fw-semibold">
            {i18n.t("roster.historySnapshotTitle", {
              ns: "ui",
              season,
              defaultValue: "{{season}} roster snapshot",
            })}
          </div>
          <div className="d-flex flex-wrap gap-2 mt-2">
            <span className="badge text-bg-secondary" data-roster-readonly-chip="">
              {i18n.t("roster.mobileReadonlyReviewChip", {
                ns: "ui",
                defaultValue: "Read-only view",
              })}
            </span>
            <span className="badge text-bg-light border">
              {i18n.t("roster.p2s03HistoricalSeasonResult", {
                ns: "ui",
                season,
                defaultValue: "{{season}} archive",
              })}
            </span>
            <span className="badge text-bg-light border">
              {i18n.t("roster.p2s03HistoricalCore", {
                ns: "ui",
                count: corePlayers.length,
                defaultValue: "{{count}} core player(s)",
              })}
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-2">
            <a
              className="btn btn-outline-secondary btn-sm"
              href={helpers.leagueUrl(["roster", teamRouteParam, season - 1])}
            >
              {i18n.t("roster.previousSeason", {
                ns: "ui",
                defaultValue: "Previous season",
              })}
            </a>
            <a
              className="btn btn-outline-secondary btn-sm"
              href={helpers.leagueUrl(["roster", teamRouteParam, season + 1])}
            >
              {i18n.t("roster.nextSeason", {
                ns: "ui",
                defaultValue: "Next season",
              })}
            </a>
          </div>
        </div>
      ) : null}

      {isPlayoffRoster ? (
        <div className="alert alert-info">
          <div className="fw-semibold">
            {i18n.t("roster.playoffSnapshotTitle", {
              ns: "ui",
              season,
              defaultValue: "{{season}} playoff roster",
            })}
          </div>
          <div className="d-flex flex-wrap gap-2 mt-2">
            <span className="badge text-bg-secondary" data-roster-readonly-chip="">
              {i18n.t("roster.mobileReadonlyReviewChip", {
                ns: "ui",
                defaultValue: "Read-only view",
              })}
            </span>
            <span className="badge text-bg-secondary">
              {i18n.t("roster.p2s03PlayoffSplitChip", {
                ns: "ui",
                defaultValue: "Playoff split",
              })}
            </span>
            <span className="badge text-bg-light border">
              {i18n.t("roster.p2s03PlayoffCore", {
                ns: "ui",
                count: corePlayers.length,
                defaultValue: "{{count}} core contributor(s)",
              })}
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-2">
            <a
              className="btn btn-outline-secondary btn-sm"
              href={helpers.leagueUrl(["roster", teamRouteParam, season])}
            >
              {i18n.t("roster.compareRegularSeason", {
                ns: "ui",
                defaultValue: "Compare regular season",
              })}
            </a>
            <a
              className="btn btn-outline-secondary btn-sm"
              href={createMobileCrossTabHref(helpers.leagueUrl(["playoffs", season]))}
            >
              {i18n.t("roster.returnToSeries", {
                ns: "ui",
                defaultValue: "Return to series",
              })}
            </a>
          </div>
        </div>
      ) : null}

      {opponentFinanceHref ? (
        <nav
          aria-label={i18n.t("roster.opponentContextLinksLabel", {
            ns: "ui",
            defaultValue: "Opponent scouting actions",
          })}
          className="more-links-single mb-3"
          data-roster-opponent-finance-entry={opponentFinanceScope}
        >
          <a href={opponentFinanceHref} onClick={seedOpponentFinanceReturn}>
            {i18n.t("roster.opponentFinancesLink", {
              ns: "ui",
              defaultValue: "View opponent finances",
            })}
          </a>
        </nav>
      ) : null}

      <RosterReadinessHero
        maxRosterSize={maxRosterSize}
        minRosterSize={minRosterSize}
        numPlayersOnCourt={numPlayersOnCourt}
        payroll={payroll}
        playerCount={playersSorted.length}
        primaryActionSurface={rosterPrimaryActionSurface}
        readiness={rosterReadiness}
        salaryCap={salaryCap}
        salaryCapType={salaryCapType}
        showRosterManagementToggle={showRosterManagementToggle}
        teamColors={t.colors}
      >
        {starterRows}
      </RosterReadinessHero>

      {fixedManagementAvailable ? (
        <div
          className="mobile-primary-cta roster-management-action"
          data-desktop-roster-management-action=""
          data-roster-management-state={rosterManagementActive ? "editing" : "browse"}
        >
          <button
            aria-pressed={rosterManagementActive}
            className="btn btn-primary mobile-primary-cta-button"
            data-roster-management-toggle=""
            onClick={() => {
              setEditingRoster((value) => !value);
            }}
            type="button"
          >
            {fixedManagementActionLabel}
          </button>
        </div>
      ) : null}

      {/* The Team tab is the mobile management workspace. Every domain module
          below opens the same sheet pattern in place; the legacy finance route
          remains only as a direct/deep-link fallback. */}
      {isUserCurrentRoster ? (
        <nav
          aria-label={i18n.t("roster.teamPagesTitle", {
            ns: "ui",
            defaultValue: "Team pages",
          })}
          className="mb-3"
          data-roster-team-links=""
        >
          <div
            className="list-group list-group-flush border rounded-3 overflow-hidden"
            data-roster-team-actions=""
          >
            <button
              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2"
              data-roster-team-action="finance_overview"
              onClick={() => setTeamFinanceSheet("overview")}
              type="button"
            >
              <span className="fw-semibold">
                {i18n.t("roster.teamFinancesLink", {
                  ns: "ui",
                  defaultValue: "Finances",
                })}
              </span>
              <span aria-hidden="true" className="text-body-secondary">
                ›
              </span>
            </button>
            <button
              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2 text-start"
              data-roster-team-action="player_contracts"
              onClick={() => setTeamFinanceSheet("contracts")}
              type="button"
            >
              <span className="fw-semibold">
                {i18n.t("roster.playerContractsLink", {
                  ns: "ui",
                  defaultValue: "Player contracts",
                })}
              </span>
              <span aria-hidden="true" className="text-body-secondary">›</span>
            </button>
            <button
              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2 text-start"
              data-roster-team-action="expense_levels"
              onClick={() => setTeamFinanceSheet("expenses")}
              type="button"
            >
              <span className="fw-semibold">
                {i18n.t("roster.expenseLevelsLink", {
                  ns: "ui",
                  defaultValue: "Expense levels",
                })}
              </span>
              <span aria-hidden="true" className="text-body-secondary">›</span>
            </button>
            <button
              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2 text-start"
              data-roster-team-action="finance_history"
              onClick={() => setTeamFinanceSheet("history")}
              type="button"
            >
              <span className="fw-semibold">
                {i18n.t("roster.financeHistoryLink", {
                  ns: "ui",
                  defaultValue: "Financial history",
                })}
              </span>
              <span aria-hidden="true" className="text-body-secondary">›</span>
            </button>
            <button
              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2 text-start"
              data-roster-team-action="injuries"
              onClick={() => setInjuriesSheetOpen(true)}
              type="button"
            >
              <span className="fw-semibold">
                {i18n.t("roster.injuryReportLink", {
                  ns: "ui",
                  defaultValue: "Injury report",
                })}
              </span>
              <span aria-hidden="true" className="text-body-secondary">
                ›
              </span>
            </button>
            <button
              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2 text-start"
              data-roster-team-action="team_history"
              onClick={() => setTeamHistorySheetOpen(true)}
              type="button"
            >
              <span className="fw-semibold">
                {i18n.t("roster.teamHistoryLink", {
                  ns: "ui",
                  defaultValue: "Team history",
                })}
              </span>
              <span aria-hidden="true" className="text-body-secondary">
                ›
              </span>
            </button>
          </div>
        </nav>
      ) : null}

      {isOtherTeamCurrentRoster ? (
        <MobileSection
          className="mb-3"
          data-roster-scouting-summary=""
          title={i18n.t("roster.scoutingSummaryTitle", {
            ns: "ui",
            defaultValue: "Scouting summary",
          })}
        >
          <div className="row g-3">
            <div className="col-12 col-lg-6">
              <h2 className="h6">
                {i18n.t("roster.coreThree", {
                  ns: "ui",
                  defaultValue: "Core three",
                })}
              </h2>
              {renderScoutingList({
                emptyText: i18n.t("roster.noScoutingTargets", {
                  ns: "ui",
                  defaultValue: "No players are available in this group.",
                }),
                players: corePlayers,
                testID: "core-three",
              })}
            </div>
            <div className="col-12 col-lg-6">
              <h2 className="h6">
                {i18n.t("roster.tradeTargets", {
                  ns: "ui",
                  defaultValue: "Trade targets",
                })}
              </h2>
              {renderScoutingList({
                emptyText: i18n.t("roster.noScoutingTargets", {
                  ns: "ui",
                  defaultValue: "No players are available in this group.",
                }),
                players: tradeTargetPlayers,
                testID: "trade-targets",
              })}
            </div>
          </div>
        </MobileSection>
      ) : null}

      {showSpectatorWarning ? (
        <p className="alert alert-danger d-inline-block">
          {i18n.t("roster.spectatorWarning", {
            ns: "ui",
            defaultValue: "The AI will handle roster management in spectator mode.",
          })}
        </p>
      ) : null}

      {rosterManagementActive ? (
        <div className="alert alert-info" data-roster-edit-feedback="">
          {i18n.t("roster.editModeFeedback", {
            ns: "ui",
            defaultValue: "Rotation edits save automatically. Use Done to return to browse mode.",
          })}
        </div>
      ) : null}

      <div
        data-roster-edit-mode={rosterManagementActive ? "editing" : "browse"}
        data-roster-readonly={canEditRoster ? "false" : "true"}
      >
        {/* On the user's own current roster the full-roster toggle lives in the
            unified team-action row above; other roster views (other team /
            historical / playoff) keep a standalone toggle here. Closed groups
            never mount their player cards (they read as duplicate entity links
            to the audit). */}
        {isOtherTeamCurrentRoster ? (
          <button
            aria-expanded={fullRosterOpen}
            className="btn btn-outline-secondary btn-sm mb-3"
            data-roster-full-roster-toggle=""
            onClick={() => setRosterDepthOpen((value) => !value)}
            type="button"
          >
            {i18n.t("roster.p0s08FullRosterSummary", {
              ns: "ui",
              defaultValue: "Open full roster",
            })}
            <span className="visually-hidden">
              {i18n.t("roster.fullRosterDepth", {
                ns: "ui",
                defaultValue: "Full roster depth",
              })}
            </span>
          </button>
        ) : null}
        {fullRosterOpen && (rosterManagementActive || isOtherTeamCurrentRoster) ? (
          <div className="mb-3">{rosterGroups}</div>
        ) : null}
      </div>

      {/* M3-1 overlays. Injuries is scoped to the user's own current roster; the
          team-history sheet serves the currently-viewed team (own or scouted),
          fetching its data on open via the M3 worker-RPC pattern. */}
      {isUserCurrentRoster ? (
        <>
          {teamFinances ? (
            <TeamFinanceSheets
              active={teamFinanceSheet}
              data={teamFinances}
              onClose={() => setTeamFinanceSheet(undefined)}
            />
          ) : null}
          <InjuriesSheet
            onClose={() => setInjuriesSheetOpen(false)}
            open={injuriesSheetOpen}
            teamName={teamDisplayName}
            tid={tid}
          />
        </>
      ) : null}
      <TeamHistorySheet
        onClose={() => setTeamHistorySheetOpen(false)}
        open={teamHistorySheetOpen}
        teamName={teamDisplayName}
        tid={tid}
      />
      {playerQuickSheet ? (
        <PlayerQuickSheet
          fullProfileHref={getRosterPlayerHref(playerQuickSheet.player.pid, playerQuickSheet.group)}
          onClose={() => setPlayerQuickSheet(undefined)}
          open
          player={playerQuickSheet.player}
          ratingsVisible={!challengeNoRatings || playerQuickSheet.player.tid === PLAYER.RETIRED}
          readonly={!isCurrentRegularSeason || spectatorOpponentRoster}
          reasonTags={getReasonTags(playerQuickSheet.player)}
          role={getRole(playerQuickSheet.player)}
          spectatorReadonly={spectatorOpponentRoster}
          tradeActionAvailable={isOtherTeamCurrentRoster && canTradeForRoster}
        />
      ) : null}
    </div>
  );
};

export default Roster;
